import { create } from 'zustand';
import type { User, AuthError } from '../interfaces/apiInterfaces/auth';
import { authService } from '../services/auth.service';
import { setCookie, getCookie, deleteCookie } from '../helpers/cookieHelper';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

interface AuthActions {
  loginWithGoogle: (googleEmail: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User) => void;
  clearError: () => void;
  checkAuthStatus: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set) => ({
  // State
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  // Actions
  loginWithGoogle: async (googleEmail: string) => {
    set({ isLoading: true, error: null });

    try {
      // Validate user (domain check + whitelist check)
      const user = await authService.validateUserLogin(googleEmail);

      // Store user in cookie with 24-hour expiration
      setCookie('qms_user', user, { 
        days: 1, // 24 hours
        secure: true, // HTTPS only
        sameSite: 'Lax' // CSRF protection
      });

      // Store user and mark as authenticated
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
    } catch (error) {
      const authError = error as AuthError;
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: authError.message
      });
      throw error;
    }
  },

  logout: () => {
    // Clear user cookie
    deleteCookie('qms_user');
    
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
  },

  setUser: (user: User) => {
    set({
      user,
      isAuthenticated: true,
      error: null
    });
  },

  clearError: () => {
    set({ error: null });
  },

  checkAuthStatus: () => {
    // Restore user from cookie if exists
    const user = getCookie<User>('qms_user');
    
    if (user) {
      // Valid cookie found, restore session
      set({
        user,
        isAuthenticated: true,
        isInitialized: true
      });
    } else {
      // No valid cookie, mark as unauthenticated
      set({
        user: null,
        isAuthenticated: false,
        isInitialized: true
      });
    }
  }
}));

// Initialize auth status from cookie immediately when store is created
useAuthStore.getState().checkAuthStatus();
